import {useTranslation} from '@aboutyou/i18n';
import {Log} from '@aboutyou/mlm';
// eslint-disable-next-line import/no-extraneous-dependencies
import {getIsDatadogInfoLogsConsentGiven} from '@aboutyou/storage';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {UnstyledButton} from '../../../atoms/Buttons/UnstyledButton';
import DefaultInfoIcon from '../../../atoms/Icon/assets/svgs/ic-info.svg';
import {
  SponsoredCanvasProps,
  useSponsoredCanvas,
} from './utils/useSponsoredCanvas';

export interface SponsoredProps extends Partial<SponsoredCanvasProps> {
  className?: string;
  label?: string;
  onClick?: (e: React.MouseEvent) => void;
  skipRenderingText?: boolean;
  isInProductTile?: boolean;
  isSponsoredProductTileUnsampledLoggingEnabled?: boolean;
}

export const Sponsored: React.FC<SponsoredProps> = ({
  className,
  label,
  onClick,
  color = 'Text.Grey',
  icon = DefaultInfoIcon,
  iconSize = 'XSS',
  iconPosition = 'right',
  skipRenderingText = false,
  isInProductTile = false,
  isSponsoredProductTileUnsampledLoggingEnabled = false,
}) => {
  const {t} = useTranslation();
  const sponsoredText = label || t('Sponsored');
  const canvasRef = useSponsoredCanvas({
    text: sponsoredText,
    skipRenderingText,
    color,
    icon,
    iconSize,
    iconPosition,
  });

  useEffect(() => {
    if (
      !skipRenderingText &&
      isInProductTile &&
      getIsDatadogInfoLogsConsentGiven() &&
      isSponsoredProductTileUnsampledLoggingEnabled
    ) {
      Log.unSampledLog(
        '[AD BLOCKING] Rendering sponsored label in product tile',
        {
          logOnlyOnce: true,
        },
      );
    }
  }, [
    isInProductTile,
    skipRenderingText,
    isSponsoredProductTileUnsampledLoggingEnabled,
  ]);

  return (
    <Wrapper
      className={className}
      onClick={e => {
        e.stopPropagation();
        onClick?.(e);
      }}
      aria-label={t('See more info')}
      // data-testid added only for unit tests
      {...(process.env.NODE_ENV === 'test' && {'data-testid': 'sponsored'})}
      tabIndex={-1}
    >
      <CanvasLabel ref={canvasRef} />
    </Wrapper>
  );
};

const Wrapper = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 16px;
  gap: 4px;
  line-height: 1.6;
`;

const CanvasLabel = styled.canvas`
  display: block;
`;
